import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../utils/contexts/UserContext";
import useDebouncedValue from "../../utils/hooks/useDebouncedValue";
import { MAX_PAGE_SIZE } from "../../utils/constants/constants";
import { Field, inputClass } from "../../components/forms/Field";
import PanelHeader from "../../components/timetable/PanelHeader";
import {
  BTN,
  Card,
  EmptyState,
  ErrorState,
  Panel,
  PanelToolbar,
  Refreshing,
  STALE,
  SearchInput,
  TABLE_HEAD,
  TableSkeleton,
} from "../../components/timetable/PanelKit";
import ModalShell from "../Templates/ModalShell";
import ConfirmModal from "../Templates/ConfirmModal";
import { formatApiError } from "../../utils/helpers/apiErrors";
import { compareText } from "../../utils/helpers/locale";
import { getRooms } from "../../services/schedulingService";
import {
  createGroup,
  deleteGroup,
  getGroups,
  updateGroup,
} from "../../services/groupService";

// ---------------------------------------------------------------------------
// Classes (groups).
//
// Everything else in the school hangs off these rows: the timetable import
// finds them by name (codes are generated server-side and never shown), the
// observation form's class picker lists them, and an Activity teaches TO one. Until this page existed
// they could only be created through the Django admin, which meant a school
// could not finish its own setup.
//
// The list is a school's ~20-40 classes, so it is fetched in one page and
// filtered in the browser — same shape as Rooms.
// ---------------------------------------------------------------------------

const rowsOf = (data) => (Array.isArray(data) ? data : data?.results || []);

export default function ClassesPage() {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);

  // Classes belong to one school; writes are own-school only (the backend
  // defaults `organization` to the caller's school, so it is never sent).
  const orgUuid = user?.organizations?.[0]?.organization?.uuid;

  const [groups, setGroups] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 200);

  const [editing, setEditing] = useState(null); // group | "new" | null
  const [deleting, setDeleting] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    getRooms({
      params: { organization: orgUuid || undefined, page_size: MAX_PAGE_SIZE },
      signal: controller.signal,
    })
      .then((data) => setRooms(rowsOf(data)))
      .catch(() => setRooms([]));
    return () => controller.abort();
  }, [orgUuid]);

  const loadGroups = useCallback(
    (signal) => {
      setIsLoading(true);
      getGroups({
        params: { organization: orgUuid || undefined, page_size: MAX_PAGE_SIZE },
        signal,
      })
        .then((data) => {
          setGroups(rowsOf(data));
          setLoadError(false);
        })
        .catch((err) => {
          if (err?.code === "ERR_CANCELED") return;
          // No toast: the failure renders as the panel below (first load) or
          // the strip above the table (refetch) — a toast would just repeat it.
          setLoadError(true);
        })
        .finally(() => setIsLoading(false));
    },
    [orgUuid],
  );

  useEffect(() => {
    const controller = new AbortController();
    loadGroups(controller.signal);
    return () => controller.abort();
  }, [loadGroups]);

  const roomOptions = useMemo(
    () =>
      rooms
        .map((room) => ({ value: room.uuid, label: room.name || room.code || "" }))
        .sort((a, b) => compareText(a.label, b.label)),
    [rooms],
  );

  const visibleGroups = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((group) => String(group.name || "").toLowerCase().includes(q));
  }, [groups, debouncedSearch]);

  const confirmDelete = async () => {
    if (!deleting) return;
    setIsDeleting(true);
    try {
      await deleteGroup({ uuid: deleting.uuid });
      toast.success(t("timetable.classes.deleted"));
      setDeleting(null);
      loadGroups();
    } catch (err) {
      toast.error(formatApiError(err, t("timetable.classes.deleteFailed")));
    } finally {
      setIsDeleting(false);
    }
  };

  // Page wrapper, back link, WIDTH and permission guard live on
  // TimetableWorkspace; rhythm and surfaces come from PanelKit.
  return (
    <>
      <Panel>
        <PanelHeader
          title={t("timetable.classes.heading")}
          count={visibleGroups.length}
          subtitle={t("timetable.classes.intro")}
        >
          {groups.length > 0 && (
            <button
              type="button"
              onClick={() => setEditing("new")}
              className={BTN.primary}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
              </svg>
              {t("timetable.classes.add")}
            </button>
          )}
        </PanelHeader>

        {groups.length > 0 && (
          <PanelToolbar>
            <SearchInput
              value={search}
              onChange={setSearch}
              ariaLabel={t("timetable.classes.searchAria")}
            />
            {Boolean(debouncedSearch) && (
              <button type="button" onClick={() => setSearch("")} className={BTN.quiet}>
                {t("common.clearFilters")}
              </button>
            )}
            {isLoading && (
              <span className="ml-auto">
                <Refreshing />
              </span>
            )}
          </PanelToolbar>
        )}

        {isLoading && groups.length === 0 ? (
          <TableSkeleton
            rows={6}
            columns={["w-32", "w-28 hidden md:block", "w-32"]}
          />
        ) : loadError && groups.length === 0 ? (
          <ErrorState
            title={t("timetable.classes.loadFailed")}
            onRetry={() => loadGroups()}
          />
        ) : groups.length === 0 ? (
          <ClassesEmptyState onAdd={() => setEditing("new")} />
        ) : visibleGroups.length === 0 ? (
          <EmptyState
            iconPath="M21 21l-4.3-4.3m1.8-4.7a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z"
            title={t("timetable.classes.noMatches")}
            hint={t("timetable.classes.noMatchesHint")}
            action={
              <button type="button" onClick={() => setSearch("")} className={BTN.secondary}>
                {t("common.clearFilters")}
              </button>
            }
          />
        ) : (
          <Card padded={false}>
            {loadError && (
              <div
                role="alert"
                className="px-6 py-2.5 bg-rose-50 text-rose-700 text-[11px] font-black border-b border-rose-100"
              >
                {t("timetable.classes.refreshFailed")}
              </div>
            )}
            <div className={`overflow-x-auto ${isLoading ? STALE : ""}`}>
              <table className="w-full text-sm" aria-busy={isLoading}>
                <thead>
                  <tr className={TABLE_HEAD}>
                    <th className="px-6 py-3">{t("timetable.classes.colName")}</th>
                    <th className="px-6 py-3 hidden md:table-cell">
                      {t("timetable.classes.colHomeRoom")}
                    </th>
                    <th className="px-6 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {visibleGroups.map((group) => (
                    <ClassRow
                      key={group.uuid}
                      group={group}
                      onEdit={() => setEditing(group)}
                      onDelete={() => setDeleting(group)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </Panel>

      {editing && (
        <ClassFormModal
          group={editing === "new" ? null : editing}
          roomOptions={roomOptions}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            loadGroups();
          }}
        />
      )}

      {deleting && (
        <ConfirmModal
          title={t("timetable.classes.deleteTitle")}
          body={t("timetable.classes.deleteBody", {
            group: deleting.name || deleting.code || "",
          })}
          confirmLabel={
            isDeleting ? t("timetable.classes.deleting") : t("timetable.classes.delete")
          }
          danger
          busy={isDeleting}
          onConfirm={confirmDelete}
          onClose={() => setDeleting(null)}
        />
      )}
    </>
  );
}

// -- table row ---------------------------------------------------------------

function ClassRow({ group, onEdit, onDelete }) {
  const { t } = useTranslation();
  const homeRoom = group.home_room_detail;

  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50">
      <td className="px-6 py-3 font-medium text-slate-900">{group.name || "—"}</td>
      <td className="px-6 py-3 text-slate-600 hidden md:table-cell">
        {homeRoom ? homeRoom.name || homeRoom.code : "—"}
      </td>
      <td className="px-6 py-3">
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={onEdit}
            className="px-2 py-1 rounded-lg text-xs font-semibold text-indigo-600 hover:bg-indigo-50 transition"
          >
            {t("timetable.classes.edit")}
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="px-2 py-1 rounded-lg text-xs font-semibold text-rose-600 hover:bg-rose-50 transition"
          >
            {t("timetable.classes.delete")}
          </button>
        </div>
      </td>
    </tr>
  );
}

// -- empty state --------------------------------------------------------------

// Unlike Rooms, an empty class list is never the right answer — a school
// without classes cannot import a timetable or record a class-linked
// observation. So this state explains what the rows unlock and leads straight
// into adding the first one.
function ClassesEmptyState({ onAdd }) {
  const { t } = useTranslation();
  const uses = [
    t("timetable.classes.emptyUse1"),
    t("timetable.classes.emptyUse2"),
    t("timetable.classes.emptyUse3"),
  ];

  return (
    <EmptyState
      iconPath="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6 1a3 3 0 10-3-3 3 3 0 003 3z"
      title={t("timetable.classes.empty")}
      hint={t("timetable.classes.emptyLead")}
      action={
        <button type="button" onClick={onAdd} className={BTN.primary}>
          {t("timetable.classes.emptyAction")}
        </button>
      }
    >
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
          {t("timetable.classes.emptyUsesTitle")}
        </p>
        <ul className="mt-2 space-y-1.5">
          {uses.map((use) => (
            <li key={use} className="flex items-start gap-2 text-xs leading-relaxed text-slate-600">
              <span aria-hidden="true" className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
              {use}
            </li>
          ))}
        </ul>
      </div>
    </EmptyState>
  );
}

// -- create / edit modal ------------------------------------------------------

function ClassFormModal({ group, roomOptions, onClose, onSaved }) {
  const { t } = useTranslation();
  const isEdit = Boolean(group);

  const [name, setName] = useState(group?.name || "");
  const [homeRoom, setHomeRoom] = useState(group?.home_room || "");
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const next = {};
    if (!name.trim()) next.name = t("timetable.classForm.errName");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSaving(true);
    try {
      const body = {
        name: name.trim(),
        home_room: homeRoom || null,
      };
      if (isEdit) {
        await updateGroup({ uuid: group.uuid, body });
        toast.success(t("timetable.classes.updated"));
      } else {
        await createGroup({ body });
        toast.success(t("timetable.classes.created"));
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err, t("timetable.classes.saveFailed")));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalShell onClose={onClose} busy={isSaving} maxWidth="max-w-lg">
      <h3 className="text-lg font-bold text-slate-900 pr-8">
        {isEdit ? t("timetable.classForm.editTitle") : t("timetable.classForm.createTitle")}
      </h3>

      <form onSubmit={submit} noValidate className="mt-5 space-y-4">
        <Field label={t("timetable.classForm.name")} error={errors.name} required>
          <input
            type="text"
            value={name}
            disabled={isSaving}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("timetable.classForm.namePlaceholder")}
            className={inputClass(errors.name)}
          />
          <p className="mt-1 text-xs text-slate-400">{t("timetable.classForm.nameHint")}</p>
        </Field>

        <Field label={t("timetable.classForm.homeRoom")}>
          <select
            value={homeRoom}
            disabled={isSaving}
            onChange={(e) => setHomeRoom(e.target.value)}
            className={inputClass(false)}
          >
            <option value="">{t("timetable.classForm.homeRoomNone")}</option>
            {roomOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-slate-400">{t("timetable.classForm.homeRoomHint")}</p>
        </Field>

        <div className="flex justify-end gap-2 pt-3 mt-3 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className={BTN.quiet}
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className={BTN.primary}
          >
            {isSaving
              ? t("common.saving")
              : isEdit
                ? t("timetable.classForm.save")
                : t("timetable.classForm.create")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
